import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TIERS = [
  {
    id: "free",
    name: "Free",
    emoji: "🆓",
    price: "$0",
    period: "forever",
    color: "#1B2A4A",
    bg: "#F7F5F2",
    features: [
      "Browse all tips",
      "3 posts per month",
      "Basic budget tracker",
      "Relaxation Room",
      "Surviving Together newsletter",
    ],
    cta: "Current tier",
  },
  {
    id: "member",
    name: "Community Member",
    emoji: "⭐",
    price: "$2",
    period: "/month or $20/yr",
    color: "#E8621A",
    bg: "#FFF6F0",
    highlight: true,
    features: [
      "Everything in Free",
      "Unlimited tip posts",
      "Full budget history",
      "All badges available",
    ],
    cta: "Coming soon",
    disabled: true,
  },
  // Community Champion tier hidden for now — kept here for future re-enable.
  // {
  //   id: "champion",
  //   name: "Community Champion",
  //   emoji: "🌟",
  //   price: "$5",
  //   period: "/month or $50/yr",
  //   color: "#2A9D8F",
  //   bg: "#F0FAF8",
  //   features: ["Everything in Member", "Early access to new features", "Exclusive Champion badge", "Direct input on new programs", "Recognition in the community"],
  //   cta: "Choose Champion",
  // },
];

export default function Membership() {
  const notify = (name) => toast.info(`${name} tier — payment coming soon!`, { description: "For now, all features are available to explore." });

  return (
    <div className="space-y-10">
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#E8621A]/10 text-[#E8621A] px-3 py-1 text-xs font-semibold tracking-widest uppercase">
          <Sparkles className="w-3 h-3" /> Membership
        </div>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold text-[#1B2A4A] leading-tight">
          Support your <span className="text-[#E8621A]">community.</span>
        </h1>
        <p className="mt-3 text-[#1B2A4A]/70">
          Pick the tier that fits. Every dollar helps us build more tools like these for neighbors who need them.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        {TIERS.map((t) => (
          <div
            key={t.id}
            data-testid={`tier-${t.id}`}
            className={`relative rounded-3xl border p-6 sm:p-8 flex flex-col ${
              t.highlight ? "border-[#E8621A] shadow-lg shadow-[#E8621A]/10 lg:-translate-y-2" : "border-[#1B2A4A]/10"
            }`}
            style={{ background: t.bg }}
          >
            {t.highlight && (
              <div className="absolute -top-3 left-6 rounded-full bg-[#E8621A] text-white px-3 py-1 text-[10px] font-bold tracking-widest uppercase">
                Most loved
              </div>
            )}
            <div className="text-3xl mb-2">{t.emoji}</div>
            <p className="font-heading text-2xl font-bold text-[#1B2A4A]">{t.name}</p>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-heading text-4xl font-bold" style={{ color: t.color }}>{t.price}</span>
              <span className="text-sm text-[#1B2A4A]/60">{t.period}</span>
            </div>
            <ul className="mt-6 space-y-2 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-[#1B2A4A]/85">
                  <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: t.color }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => !t.disabled && notify(t.name)}
              data-testid={`tier-cta-${t.id}`}
              disabled={t.id === "free" || t.disabled}
              className={`mt-6 h-11 rounded-full font-semibold transition-colors duration-200 ${
                t.id === "free" ? "bg-[#1B2A4A]/10 text-[#1B2A4A] hover:bg-[#1B2A4A]/10 cursor-default"
                : t.disabled ? "bg-[#1B2A4A]/10 text-[#1B2A4A]/70 hover:bg-[#1B2A4A]/10 cursor-not-allowed opacity-80"
                : t.highlight ? "bg-[#E8621A] hover:bg-[#D05615] text-white"
                : "bg-[#1B2A4A] hover:bg-[#1B2A4A]/90 text-white"
              }`}
            >
              {t.cta}
            </Button>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-[#1B2A4A]/50">
        Payments are not enabled yet — this is a preview of upcoming tiers.
      </p>
    </div>
  );
}
